import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import clsx from 'clsx';
import {
  Page, PageHeader, card, cardHead, th, td, thMd, tdMd, thSm, tdSm, trHover, input, select, selectStyle,
  btnPrimary, btnSecondary, btnDangerSolid, iconBtn, iconBtnOk, iconBtnDanger,
  StatusChip, StatusSelect, EmptyState, Modal, Badge,
} from '../components/ui';
import { IconSearch, IconDownload, IconEye, IconCheck, IconTrash, IconChevronLeft, IconClipboard } from '../components/Icons';

// Field label mapping for readable display
const fieldLabelMap: Record<string, string> = {
  // Personal info
  name: '姓名',
  p1_name: '姓名',
  p2_name: '姓名 (2)',
  p3_name: '姓名 (3)',
  email: '電子郵件',
  contact_email: '聯絡 Email',
  phone: '電話',
  phone_contact: '聯絡電話',
  p1_phone: '電話',
  p2_phone: '電話 (2)',
  p3_phone: '電話 (3)',
  line_name: 'LINE 名稱',
  // ID
  taiwan_id: '身分證字號',
  id_number: '身分證字號',
  p1_id_number: '身分證字號',
  p2_id_number: '身分證字號 (2)',
  p3_id_number: '身分證字號 (3)',
  // Payment
  remittance_bank: '匯款銀行',
  remittance_date: '匯款日期',
  remit_bank: '匯款銀行',
  remit_date: '匯款日期',
  last_5_digits: '帳號末五碼',
  remit_last5: '帳號末五碼',
  remittance_amount: '匯款金額',
  remit_amount: '匯款金額',
  payment_proof: '付款證明',
  // Shipping
  shipping_address: '寄送地址',
  address: '地址',
  postal_code: '郵遞區號',
  city: '縣市',
  district: '區域',
  // Emergency
  emergency_contact: '緊急聯絡人',
  emergency_name: '緊急聯絡人',
  emergency_phone: '緊急聯絡電話',
  emergency_relation: '緊急聯絡人關係',
  // Race info
  category: '組別',
  race_category: '比賽組別',
  shirt_size: '衣服尺寸',
  estimated_time: '預估完賽時間',
  // Other
  birthday: '生日',
  birth_date: '出生日期',
  gender: '性別',
  blood_type: '血型',
  club: '社團/跑團',
  remarks: '備註',
  notes: '備註',
  agreed: '同意條款',
  agreement: '同意條款',
  agree_checkbox: '同意條款',
};

function getFieldLabel(key: string): string {
  return fieldLabelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function registrantName(reg: any) {
  return reg.formData?.name || reg.formData?.p1_name || '未填寫';
}

const statusOptions = [
  { value: 'pending', label: '待審核' },
  { value: 'verified', label: '已確認' },
  { value: 'rejected', label: '已拒絕' },
];

// Detail Modal Component
function DetailModal({ registration, onClose }: { registration: any; onClose: () => void }) {
  if (!registration) return null;

  const formEntries = Object.entries(registration.formData || {});

  return (
    <Modal
      title="報名詳情"
      description={`${registrantName(registration)} 於 ${new Date(registration.createdAt).toLocaleString('zh-TW')} 送出`}
      onClose={onClose}
      size="lg"
      footer={<button type="button" onClick={onClose} className={btnSecondary}>關閉</button>}
    >
      <div className="flex flex-col gap-6">
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg border border-line px-4 py-3">
            <dt className="text-xs text-muted mb-1">活動</dt>
            <dd className="font-medium">{registration.eventTitle || registration.eventId}</dd>
          </div>
          <div className="rounded-lg border border-line px-4 py-3">
            <dt className="text-xs text-muted mb-1">組別</dt>
            <dd className="font-medium">{registration.selectedCategoryId || '-'}</dd>
          </div>
          <div className="rounded-lg border border-line px-4 py-3">
            <dt className="text-xs text-muted mb-1">付款狀態</dt>
            <dd><StatusChip status={registration.paymentStatus} /></dd>
          </div>
          <div className="rounded-lg border border-line px-4 py-3">
            <dt className="text-xs text-muted mb-1">LINE 使用者</dt>
            <dd className="font-medium">{registration.userDisplayName || '-'}</dd>
          </div>
        </dl>

        {formEntries.length > 0 ? (
          <div>
            <h4 className="text-sm font-semibold mb-2">表單資料</h4>
            <div className="rounded-lg border border-line divide-y divide-line-soft">
              {formEntries.map(([key, value]) => (
                <div key={key} className="flex gap-4 px-4 py-2.5">
                  <div className="w-1/3 text-[13px] text-muted">{getFieldLabel(key)}</div>
                  <div className="w-2/3 text-sm break-all">
                    {typeof value === 'object' ? JSON.stringify(value) : String(value) || '-'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <EmptyState title="無表單資料" className="py-6" />
        )}
      </div>
    </Modal>
  );
}

// Delete Confirmation Modal
function DeleteModal({ registration, onConfirm, onClose }: {
  registration: any;
  onConfirm: () => void;
  onClose: () => void;
}) {
  if (!registration) return null;
  const name = registration.formData?.name || registration.formData?.p1_name || registration.userDisplayName || '此報名';

  return (
    <Modal
      title="刪除報名資料？"
      description={<>將永久刪除 <span className="font-medium text-ink">{name}</span> 的報名資料。此操作無法復原。</>}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <button type="button" onClick={onClose} className={btnSecondary}>取消</button>
          <button type="button" onClick={onConfirm} className={btnDangerSolid}>刪除</button>
        </>
      }
    />
  );
}

export default function Registrations() {
  const { eventId } = useParams();
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(eventId || 'all');
  const [detailModal, setDetailModal] = useState<any>(null);
  const [deleteModal, setDeleteModal] = useState<any>(null);

  useEffect(() => {
    async function loadData() {
      try {
        const [regsRes, eventsRes] = await Promise.all([
          api.getRegistrations(eventId),
          api.getEvents(),
        ]);

        if (regsRes.success) {
          setRegistrations(regsRes.data);
        }
        if (eventsRes.success) {
          setEvents(eventsRes.data);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [eventId]);

  const filteredRegistrations = registrations.filter((reg) => {
    const searchLower = search.toLowerCase();
    const matchesSearch = !search ||
      (reg.formData?.name?.toLowerCase().includes(searchLower)) ||
      (reg.formData?.p1_name?.toLowerCase().includes(searchLower)) ||
      (reg.formData?.line_name?.toLowerCase().includes(searchLower)) ||
      (reg.userDisplayName?.toLowerCase().includes(searchLower)) ||
      (reg.formData?.email?.toLowerCase().includes(searchLower)) ||
      (reg.formData?.phone?.includes(search));
    const matchesStatus = statusFilter === 'all' || reg.paymentStatus === statusFilter;
    const matchesEvent = selectedEvent === 'all' || reg.eventId === selectedEvent;
    return matchesSearch && matchesStatus && matchesEvent;
  });

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await api.updateRegistration(id, { paymentStatus: newStatus });
      setRegistrations(registrations.map((r) =>
        r.id === id ? { ...r, paymentStatus: newStatus } : r
      ));
    } catch (error) {
      console.error('Failed to update registration:', error);
      alert('更新失敗');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.deleteRegistration(id);
      setRegistrations(registrations.filter((r) => r.id !== id));
      setDeleteModal(null);
    } catch (error) {
      console.error('Failed to delete registration:', error);
      alert('刪除失敗');
    }
  };

  const exportToCsv = () => {
    if (filteredRegistrations.length === 0) {
      alert('沒有資料可匯出');
      return;
    }

    // Get all unique keys from formData
    const allKeys = new Set<string>();
    filteredRegistrations.forEach((reg) => {
      Object.keys(reg.formData || {}).forEach((key) => allKeys.add(key));
    });

    const headers = ['ID', 'LINE暱稱', '活動', '組別', '付款狀態', '建立時間', ...Array.from(allKeys)];
    const rows = filteredRegistrations.map((reg) => [
      reg.id,
      reg.userDisplayName || '',
      reg.eventTitle || reg.eventId,
      reg.selectedCategoryId || '',
      reg.paymentStatus,
      reg.createdAt,
      ...Array.from(allKeys).map((key) => reg.formData?.[key] || ''),
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `registrations-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const currentEvent = eventId ? events.find((e) => e.id === eventId) : undefined;
  const pendingCount = registrations.filter((r) => r.paymentStatus === 'pending').length;
  const isFiltered = !!search || statusFilter !== 'all' || selectedEvent !== 'all';

  return (
    <Page>
      {detailModal && (
        <DetailModal registration={detailModal} onClose={() => setDetailModal(null)} />
      )}
      {deleteModal && (
        <DeleteModal
          registration={deleteModal}
          onConfirm={() => handleDelete(deleteModal.id)}
          onClose={() => setDeleteModal(null)}
        />
      )}

      <PageHeader
        back={eventId && (
          <Link to="/registrations" aria-label="返回全部報名" title="返回全部報名" className={iconBtn}>
            <IconChevronLeft size={16} />
          </Link>
        )}
        title={currentEvent ? currentEvent.title : '報名列表'}
        subtitle={
          loading ? undefined :
          currentEvent ? `${registrations.length} 筆報名，${pendingCount} 筆待審核` :
          `共 ${registrations.length} 筆報名，${pendingCount} 筆待審核`
        }
        actions={
          <button type="button" onClick={exportToCsv} className={btnSecondary}>
            <IconDownload size={16} />
            匯出 CSV
          </button>
        }
      />

      <section className={clsx(card, 'flex flex-col')}>
        {/* Filters */}
        <div className={cardHead}>
          <div className="flex flex-wrap gap-2 flex-1 min-w-0">
            <div className="relative w-full sm:w-72">
              <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9AA3B0] pointer-events-none" />
              <input
                type="search"
                placeholder="搜尋姓名、暱稱、Email、電話"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="搜尋報名"
                className={clsx(input, 'pl-9')}
              />
            </div>
            {!eventId && (
              <select
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
                aria-label="篩選活動"
                className={clsx(select, 'max-w-[260px]')}
                style={selectStyle}
              >
                <option value="all">全部活動</option>
                {events.map((event) => (
                  <option key={event.id} value={event.id}>{event.title}</option>
                ))}
              </select>
            )}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="篩選狀態"
              className={select}
              style={selectStyle}
            >
              <option value="all">全部狀態</option>
              <option value="pending">待審核</option>
              <option value="verified">已確認</option>
              <option value="rejected">已拒絕</option>
            </select>
          </div>
          <span className="tabular text-[13px] text-muted">
            {isFiltered ? `符合 ${filteredRegistrations.length} 筆` : ''}
          </span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={th}>報名者</th>
                {!eventId && <th className={thMd}>活動</th>}
                <th className={thSm}>組別</th>
                <th className={thMd}>聯絡方式</th>
                <th className={th}>付款狀態</th>
                <th className={thMd}>報名時間</th>
                <th className={th}><span className="sr-only">操作</span></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-muted text-sm">載入中</td></tr>
              ) : filteredRegistrations.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-0">
                    <EmptyState
                      icon={<IconClipboard size={28} />}
                      title={registrations.length === 0 ? '尚無報名資料' : '沒有符合條件的報名'}
                      hint={registrations.length === 0 ? '報名者透過 LINE 送出表單後會顯示在這裡。' : '試試其他關鍵字、活動或狀態。'}
                    />
                  </td>
                </tr>
              ) : filteredRegistrations.map((reg) => (
                <tr key={reg.id} className={trHover}>
                  <td className={td}>
                    <div className="flex flex-col leading-snug">
                      <span className="font-medium">{registrantName(reg)}</span>
                      <span className="text-xs text-muted">
                        {reg.userDisplayName || reg.formData?.line_name || '-'}
                      </span>
                      {!eventId && (
                        <span className="md:hidden text-xs text-muted truncate max-w-[150px]">{reg.eventTitle || reg.eventId}</span>
                      )}
                      {reg.selectedCategoryId && (
                        <span className="sm:hidden text-xs text-muted truncate max-w-[150px]">{reg.selectedCategoryId}</span>
                      )}
                    </div>
                  </td>
                  {!eventId && <td className={clsx(tdMd, 'max-w-[260px] truncate')}>{reg.eventTitle || reg.eventId}</td>}
                  <td className={tdSm}>{reg.selectedCategoryId || '-'}</td>
                  <td className={tdMd}>
                    <div className="flex flex-col leading-snug">
                      <span>{reg.formData?.email || '-'}</span>
                      <span className="tabular text-xs text-muted">{reg.formData?.phone || reg.formData?.p1_phone || '-'}</span>
                    </div>
                  </td>
                  <td className={td}>
                    <StatusSelect
                      value={reg.paymentStatus}
                      onChange={(v) => handleStatusChange(reg.id, v)}
                      options={statusOptions}
                      ariaLabel={`${registrantName(reg)} 的付款狀態`}
                    />
                  </td>
                  <td className={clsx(tdMd, 'tabular text-muted')}>{formatDate(reg.createdAt)}</td>
                  <td className={clsx(td, 'text-right')}>
                    <div className="inline-flex items-center gap-1.5">
                      <button type="button" onClick={() => setDetailModal(reg)} aria-label="查看詳情" title="查看詳情" className={iconBtn}>
                        <IconEye size={16} />
                      </button>
                      {reg.paymentStatus !== 'verified' && (
                        <button type="button" onClick={() => handleStatusChange(reg.id, 'verified')} aria-label="確認付款" title="確認付款" className={iconBtnOk}>
                          <IconCheck size={16} />
                        </button>
                      )}
                      <button type="button" onClick={() => setDeleteModal(reg)} aria-label="刪除" title="刪除" className={iconBtnDanger}>
                        <IconTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && filteredRegistrations.length > 0 && (
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-line-soft text-[13px] text-muted">
            <span className="tabular">顯示 {filteredRegistrations.length} 筆</span>
            {pendingCount > 0 && statusFilter === 'all' && (
              <button type="button" onClick={() => setStatusFilter('pending')} className="inline-flex items-center gap-2 hover:text-ink">
                <Badge tone="warn">{pendingCount} 筆待審核</Badge>
              </button>
            )}
          </div>
        )}
      </section>
    </Page>
  );
}
